<div class="w-full py-4 pb-6">
  <div
    class="w-full flex flex-col gap-4 rounded-xl border border-gray-200 p-5 shadow-sm bg-white"
    *ngIf="(data?.length ?? 0) > 0"
  >
    <h1 class="text-2xl font-bold text-gray-900" *ngIf="title">{{ title }}</h1>
    <div class="flex flex-wrap items-center gap-3 justify-start" *ngIf="showSearch || showFilter">
      <!-- Search Input -->
      <div
        *ngIf="showSearch"
        class="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-gray-300 bg-white shadow-sm min-w-[280px]"
      >
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1110.5 3a7.5 7.5 0 016.15 12.65z"
          />
        </svg>
        <input
          type="text"
          [placeholder]="searchPlaceholder"
          [(ngModel)]="searchValue"
          (input)="onSearchInput($event)"
          class="border-0 outline-none bg-transparent text-sm font-medium text-gray-900 min-w-[320px] placeholder:text-gray-400"
        />
        <button
          *ngIf="searchValue"
          (click)="clearSearch()"
          class="text-gray-400 hover:text-gray-600 transition"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <!-- Filters -->
      <div
        *ngFor="let filter of filterOptions; let i = index"
        class="relative"
        appClickOutside
        (appClickOutside)="closeFilter(filter.key)"
      >
        <button
          type="button"
          (click)="onFilterClick(filter.key)"
          class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-500/20 bg-primary-500/8 text-primary-700 font-semibold capitalize hover:border-gray-500 hover:bg-primary-500/15 transition"
          [class.border-primary-600]="FilterStates[filter.key].selected"
        >
          <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span>{{
            FilterStates[filter.key].selected
              ? (FilterStates[filter.key].selected | removeUnderscore)
              : filter.label
          }}</span>
          <svg
            *ngIf="FilterStates[filter.key]?.selected"
            (click)="clearFilter(filter.key, $event)"
            class="w-4 h-4 text-gray-500 hover:text-red-600 transition cursor-pointer"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <!-- Dropdown Menu -->
        <div
          *ngIf="FilterStates[filter.key]?.isOpen"
          class="absolute z-10 mt-2 w-64 rounded-lg shadow-lg bg-white border border-gray-200 py-2 max-h-80 overflow-y-auto"
        >
          <button
            *ngFor="let option of filter.options"
            (click)="onFilterSelect(filter.key, option)"
            class="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition cursor-pointer"
            [class.bg-primary-50]="isFilterSelected(filter.key, option)"
            [class.font-semibold]="isFilterSelected(filter.key, option)"
          >
            <span class="flex-1 text-left">{{ option | removeUnderscore }}</span>
            <svg
              *ngIf="isFilterSelected(filter.key, option)"
              class="w-5 h-5 text-primary-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Clear All Filters Button -->
      <button
        *ngIf="selectedFilter || searchValue || hasActiveAdditionalFilters()"
        (click)="clearAllFilters()"
        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-red-700 font-semibold hover:border-red-500 hover:bg-red-500/15 transition"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        Clear All
      </button>
    </div>

    <!-- Table -->
    <div
      class="overflow-x-auto bg-white/95 rounded-2xl border border-gray-200 shadow-sm p-1 px-2 pb-4"
    >
      <table class="min-w-full table-auto divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              *ngFor="let col of config.fields"
              class="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider text-left"
            >
              {{ col.name }}
            </th>
            <th
              *ngIf="config.hasAction"
              class="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider text-right"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr
            *ngFor="let row of paginatedData; let i = index"
            (click)="toggleExpand(row)"
            class="hover:bg-gray-50 hover:shadow transition-transform duration-150 cursor-pointer"
          >
            <td *ngFor="let col of config.fields" class="px-5 py-4 text-sm text-gray-800">
              <ng-container *ngIf="col.isCompanyName">
                <div class="flex items-center gap-2 whitespace-nowrap">
                  <span class="font-semibold">{{ row[col.key] | uppercase }}</span>
                </div>
              </ng-container>

              <ng-container *ngIf="col.isNumber">
                {{ row[col.key] | number:'1.0-2' }}
              </ng-container>

              <ng-container *ngIf="col.isCompanyType">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs uppercase font-semibold bg-gray-100 border border-gray-200 text-gray-700 whitespace-nowrap"
                >
                  {{ row[col.key] | removeUnderscore }}
                </span>
              </ng-container>

              <ng-container *ngIf="col.isAmendmentType">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs uppercase font-semibold bg-gray-100 border border-gray-200 text-gray-700 whitespace-nowrap"
                >
                  {{ (row[col.key] | removeUnderscore) || '-' }}
                </span>
              </ng-container>

              <ng-container *ngIf="col.isStatus">
                <span
                  class="px-2 py-1 text-sm rounded-lg border shadow-sm whitespace-nowrap"
                  [ngClass]="getStatusClass(row[col.key])"
                >
                  {{
                    row[col.key] === EStatus.FINALLY_APPROVED
                      ? 'Approved'
                      : row[col.key] === EStatus.FINALLY_REJECTED
                        ? 'Rejected'
                        : row[col.key] === EStatus.PARTIALLY_REVIEWED
                          ? 'Reviewed'
                          : (row[col.key] | removeUnderscore)
                  }}
                </span>
              </ng-container>

              <ng-container *ngIf="col.isDate">
                {{ row[col.key] | date: 'dd MMM yyyy' }}
              </ng-container>

              <ng-container
                *ngIf="
                  !col.isCompanyName &&
                  !col.isNumber &&
                  !col.isCompanyType &&
                  !col.isStatus &&
                  !col.isDate &&
                  !col.isApplicantNumber &&
                  !col.isAmendmentType
                "
              >
                {{ row[col.key] }}
              </ng-container>
            </td>

            <td *ngIf="config.hasAction" class="px-5 py-4 text-sm text-gray-800 text-right">
              <div class="flex items-center justify-end gap-2">
                <ng-container *ngFor="let a of actionField?.actions">
                  <button
                    *ngIf="shouldShowActionIcon(a, row) && a !== EActionEvent.APPLY_NOW && a !== EActionEvent.PAY_CERTIFICATE"
                    (click)="onAction(a, row); $event.stopPropagation()"
                    class="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-gray-50 hover:bg-primary-50 hover:border-gray-300 transition"
                  >
                    <mat-icon class="{{ getActionIcon(a).color }} text-base">{{
                      getActionIcon(a).icon
                    }}</mat-icon>

                    <span class="sr-only">{{ getActionTooltip(a) }}</span>
                  </button>
                  <button
                    *ngIf="a === EActionEvent.APPLY_NOW && shouldShowActionIcon(a, row)"
                    (click)="onAction(a, row); $event.stopPropagation()"
                    class="group flex items-center gap-1 px-2 py-1.5 rounded bg-gradient-to-r from-[#1d588a] to-[#163f63] text-white text-xs font-medium hover:from-[#16446b] hover:to-[#0f2d47] transition-all duration-200 shadow hover:shadow-md hover:scale-105 active:scale-100"
                  >
                    <span>Apply Now</span>
                    <mat-icon
                      class="text-white text-sm group-hover:translate-x-1 transition-transform duration-200"
                    >
                      arrow_forward
                    </mat-icon>
                  </button>
                   <button
                    *ngIf="a === EActionEvent.PAY_CERTIFICATE && shouldShowActionIcon(a, row)"
                    (click)="onAction(a, row); $event.stopPropagation()"
                    class="group flex items-center gap-1 px-2 py-1.5 rounded bg-gradient-to-r from-[#1d588a] to-[#163f63] text-white text-xs font-medium hover:from-[#16446b] hover:to-[#0f2d47] transition-all duration-200 shadow hover:shadow-md hover:scale-105 active:scale-100"
                  >
                    <span>Pay Certificate</span>
                  </button>
                </ng-container>
              </div>
            </td>
          </tr>
          <!-- Expanded Row -->
          <!-- <tr *ngIf="expandedRow">
            <td [attr.colspan]="displayedColumns.length" class="p-0 border-0">
              <div class="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl shadow p-6 my-2 space-y-6">
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                    <h4 class="text-lg font-bold mb-4">Application Details</h4>
                    <div *ngFor="let c of config.fields" class="flex justify-between py-1">
                      <span class="font-semibold text-gray-600">{{ c.name }}</span>
                      <span class="text-gray-900">{{ expandedDetails?.[c.key] ?? '—' }}</span>
                    </div>
                  </div>

                  <div class="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                    <h4 class="text-lg font-bold mb-4">Shareholders</h4>
                    <div *ngIf="expandedDetails?.shareholders?.length; else noShares" class="flex flex-col gap-2">
                      <div *ngFor="let s of expandedDetails.shareholders" class="flex justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200">
                        <span>{{ s.name }}</span>
                        <span class="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-sm font-bold">{{ s.percentage }}%</span>
                      </div>
                    </div>
                    <ng-template #noShares>
                      <div class="text-center text-gray-400 py-6">No shareholders listed</div>
                    </ng-template>
                  </div>
                </div>

                <div class="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                  <h4 class="text-lg font-bold mb-4">Attachments</h4>
                  <div *ngIf="expandedDetails?.files?.length; else noFiles" class="flex flex-col gap-2">
                    <div *ngFor="let f of expandedDetails.files" class="flex justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-primary-50 hover:border-primary-300 transition">
                      <span class="text-gray-800 font-medium">{{ f.fileName }}</span>
                      <div class="flex gap-2">
                        <button (click)="onAction(EActionEvent.DOWNLOAD, getDownloadRow(expandedRow, f))" class="px-2 py-1 rounded border border-gray-300 hover:bg-primary-100 transition text-sm font-medium">Download</button>
                        <button (click)="onAction(EActionEvent.EYE, expandedRow)" class="px-2 py-1 rounded border border-gray-300 hover:bg-primary-100 transition text-sm font-medium">Preview</button>
                      </div>
                    </div>
                  </div>
                  <ng-template #noFiles>
                    <div class="text-center text-gray-400 py-6">No files attached</div>
                  </ng-template>
                </div>

                <div class="flex gap-4 pt-4 border-t border-gray-200" *ngIf="showActionsFor(expandedRow)">
                  <button (click)="onApprove(expandedRow)" class="flex items-center gap-2 px-6 py-3 font-semibold bg-green-500 text-white rounded-lg hover:shadow-lg transition">
                    ✅ Approve Application
                  </button>
                  <button (click)="onReject(expandedRow)" class="flex items-center gap-2 px-6 py-3 font-semibold bg-red-500 text-white rounded-lg hover:shadow-lg transition">
                    ❌ Reject Application
                  </button>
                </div>

              </div>
            </td>
          </tr> -->
        </tbody>
      </table>
    </div>

    <div class="flex justify-between items-center mt-4 text-gray-700">
      <div>
        Showing {{ currentPageStart + 1 }} - {{ currentPageEnd }} of
        {{ effectiveTotalRecords }} entries
      </div>
      <div class="flex items-center gap-2">
        <button
          (click)="prevPage()"
          [disabled]="currentPage === 1"
          class="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 transition"
        >
          Prev
        </button>
        <span>Page {{ currentPage }} of {{ effectiveTotalPages }}</span>
        <button
          (click)="nextPage()"
          [disabled]="currentPage === effectiveTotalPages"
          class="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 transition"
        >
          Next
        </button>
      </div>
    </div>
  </div>
  <div
    *ngIf="isLoading"
    class="flex flex-col items-center justify-center py-16 rounded-xl border border-gray-200 shadow-sm"
  >
    <mat-spinner diameter="50"></mat-spinner>
    <p class="mt-4 text-gray-500 text-sm">Loading data...</p>
  </div>

  <div
    *ngIf="data?.length === 0 && !isLoading"
    class="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm text-gray-400"
  >
    <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <p class="mt-2">No data found</p>
  </div>
</div>
